import { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  UsersThree,
  Door,
} from "@phosphor-icons/react";
import { selectIsAdmin, selectTherapistId } from "../../../store/slices/authSlice";
import {
  useGetTeamAvailabilityQuery,
  useGetAbsencesQuery,
  useGetAppointmentsQuery,
} from "../../../store/services/calendarApi";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import { useGetRoomsQuery } from "../../../store/services/roomsApi";
import { isAssignableTherapist } from "../../../config/permissionsRegistry";
import {
  absencesForDay,
  absenceLabel,
  canSeeAbsenceDetails,
  toLocalDateStr,
} from "../../../utils/absenceUtils";
import { getWeekStart, HEBREW_DAYS_SHORT } from "../../../utils/dateUtils";
import TherapistCalendarModal from "../TherapistCalendarModal";
import ContentLoader from "../../shared/ContentLoader";
import "./TeamCoverageGrid.css";

const MOBILE_BREAKPOINT = 768;
const DEFAULT_SESSION_MINUTES = 45;

const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const windowMinutes = (w) => {
  const s = toMinutes(w?.start);
  const e = toMinutes(w?.end);
  if (s === null || e === null || e <= s) return 0;
  return e - s;
};

const therapistKey = (t) => t?.id || t?.uid || "";
const therapistFullName = (t) =>
  `${t?.firstName || ""} ${t?.lastName || ""}`.trim() || "מטפל/ת";

const formatDayNumber = (d) => `${d.getDate()}/${d.getMonth() + 1}`;

const formatWeekRange = (days) => {
  if (!days.length) return "";
  const first = days[0];
  const last = days[days.length - 1];
  return `${formatDayNumber(first)} – ${formatDayNumber(last)}/${last.getFullYear()}`;
};

/**
 * TeamCoverageGrid — weekly "who works when" grid for the whole team.
 *
 * Rows = assignable therapists, columns = Sun..Sat. Each cell shows the
 * therapist's working-hour windows, blocked windows, absence bands and a
 * booked/capacity chip. Clicking a cell opens TherapistCalendarModal on that
 * day (the modal's own gating decides whether editing is allowed).
 *
 * One batched availability query, one absences query, one appointments query
 * per week — never per-therapist hooks.
 */
function TeamCoverageGrid({
  clinicId,
  canEditAvailability = false,
  showTherapistNames = true,
}) {
  const isAdmin = useSelector(selectIsAdmin);
  const viewerId = useSelector(selectTherapistId);

  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [selectedTherapistIds, setSelectedTherapistIds] = useState([]);
  const [onlyAvailableToday, setOnlyAvailableToday] = useState(false);
  const [roomFilter, setRoomFilter] = useState("");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [mobileDayIndex, setMobileDayIndex] = useState(() => new Date().getDay());
  const [openCell, setOpenCell] = useState(null); // { therapist, date }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ---- Week geometry --------------------------------------------------------
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      d.setHours(0, 0, 0, 0);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const weekDayStrs = useMemo(() => weekDays.map(toLocalDateStr), [weekDays]);

  const range = useMemo(() => {
    const start = new Date(weekDays[0]);
    const end = new Date(weekDays[6]);
    end.setHours(23, 59, 59, 999);
    return {
      startISO: start.toISOString(),
      endISO: end.toISOString(),
      from: weekDayStrs[0],
      to: weekDayStrs[6],
    };
  }, [weekDays, weekDayStrs]);

  const todayStr = toLocalDateStr(new Date());
  const todayInWeek = weekDayStrs.includes(todayStr);
  const isCurrentWeek = todayInWeek;

  // ---- Data -----------------------------------------------------------------
  const { data: clinicTherapists = [], isLoading: therapistsLoading } =
    useGetClinicTherapistsQuery({ clinicId }, { skip: !clinicId });

  const therapists = useMemo(
    () =>
      clinicTherapists
        .filter(isAssignableTherapist)
        .slice()
        .sort((a, b) => therapistFullName(a).localeCompare(therapistFullName(b), "he")),
    [clinicTherapists]
  );

  const therapistIds = useMemo(() => therapists.map(therapistKey).filter(Boolean), [therapists]);

  const { data: availabilityRows = [], isLoading: availabilityLoading } =
    useGetTeamAvailabilityQuery(
      { therapistIds, startDate: range.startISO, endDate: range.endISO },
      { skip: !clinicId || therapistIds.length === 0 }
    );

  const { data: absences = [] } = useGetAbsencesQuery(
    { clinicId, from: range.from, to: range.to },
    { skip: !clinicId }
  );

  // Non-admins may not be allowed to read all appointments; on error we
  // simply show no booking data (utilization chips / room filter degrade).
  const { data: appointmentsData, error: appointmentsError } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId: null,
      startDate: range.startISO,
      endDate: range.endISO,
    },
    { skip: !clinicId }
  );
  const appointments = useMemo(
    () => (appointmentsError ? [] : appointmentsData || []),
    [appointmentsData, appointmentsError]
  );

  const { data: rooms = [] } = useGetRoomsQuery({ clinicId }, { skip: !clinicId });
  const activeRooms = useMemo(() => rooms.filter((r) => r.isActive !== false), [rooms]);

  // ---- Derived maps ---------------------------------------------------------
  // therapistId -> dateStr -> { slots, blocked }
  const availabilityByTherapist = useMemo(() => {
    const map = {};
    availabilityRows.forEach((row) => {
      const byDay = {};
      (row.slots || []).forEach((day) => {
        if (!day?.date) return;
        byDay[day.date] = {
          slots: Array.isArray(day.slots) ? day.slots : [],
          blocked: Array.isArray(day.blocked) ? day.blocked : [],
        };
      });
      map[row.therapistId] = byDay;
    });
    return map;
  }, [availabilityRows]);

  // therapistId -> dateStr -> booked count (non-cancelled, non-blocked)
  const bookedByTherapist = useMemo(() => {
    const map = {};
    appointments.forEach((apt) => {
      if (!apt || apt.isBlockedTime === true) return;
      if (apt.status === "cancelled" || apt.isDeleted === true) return;
      if (!apt.therapistId || !apt.startTime) return;
      const d = new Date(apt.startTime);
      if (Number.isNaN(d.getTime())) return;
      const dateStr = toLocalDateStr(d);
      if (!map[apt.therapistId]) map[apt.therapistId] = {};
      map[apt.therapistId][dateStr] = (map[apt.therapistId][dateStr] || 0) + 1;
    });
    return map;
  }, [appointments]);

  // roomId -> Set(therapistId) with >= 1 appointment this week
  const therapistsByRoom = useMemo(() => {
    const map = {};
    appointments.forEach((apt) => {
      if (!apt?.roomId || !apt.therapistId) return;
      if (apt.status === "cancelled" || apt.isBlockedTime === true) return;
      if (!map[apt.roomId]) map[apt.roomId] = new Set();
      map[apt.roomId].add(apt.therapistId);
    });
    return map;
  }, [appointments]);

  const getCell = useCallback(
    (therapist, dateStr) => {
      const id = therapistKey(therapist);
      const day = availabilityByTherapist[id]?.[dateStr] || { slots: [], blocked: [] };
      const dayAbsences = absencesForDay(absences, dateStr, id);
      const totalMinutes = day.slots.reduce((sum, w) => sum + windowMinutes(w), 0);
      const sessionMinutes =
        parseInt(therapist.preferredSessionDuration, 10) > 0
          ? parseInt(therapist.preferredSessionDuration, 10)
          : DEFAULT_SESSION_MINUTES;
      const capacity = Math.floor(totalMinutes / sessionMinutes);
      const booked = bookedByTherapist[id]?.[dateStr] || 0;
      return {
        slots: day.slots,
        blocked: day.blocked,
        absences: dayAbsences,
        capacity,
        booked,
        hasWindows: day.slots.length > 0,
      };
    },
    [availabilityByTherapist, absences, bookedByTherapist]
  );

  // ---- Filters --------------------------------------------------------------
  const visibleTherapists = useMemo(() => {
    let list = therapists;
    if (selectedTherapistIds.length > 0) {
      const sel = new Set(selectedTherapistIds);
      list = list.filter((t) => sel.has(therapistKey(t)));
    }
    if (roomFilter) {
      const set = therapistsByRoom[roomFilter] || new Set();
      list = list.filter((t) => set.has(therapistKey(t)));
    }
    if (onlyAvailableToday && todayInWeek) {
      list = list.filter((t) => {
        const cell = getCell(t, todayStr);
        return cell.hasWindows && cell.absences.length === 0;
      });
    }
    return list;
  }, [
    therapists,
    selectedTherapistIds,
    roomFilter,
    therapistsByRoom,
    onlyAvailableToday,
    todayInWeek,
    todayStr,
    getCell,
  ]);

  const toggleTherapist = (id) => {
    setSelectedTherapistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ---- Navigation -----------------------------------------------------------
  const shiftWeek = (delta) => {
    setWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + delta * 7);
      return getWeekStart(next);
    });
  };
  const goToday = () => {
    setWeekStart(getWeekStart(new Date()));
    setMobileDayIndex(new Date().getDay());
  };

  const handleCellOpen = (therapist, date) => {
    setOpenCell({ therapist, date });
  };

  const handleCellKeyDown = (e, therapist, date) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCellOpen(therapist, date);
    }
  };

  const daysToRender = isMobile ? [weekDays[mobileDayIndex] || weekDays[0]] : weekDays;
  const isLoading = therapistsLoading || (therapistIds.length > 0 && availabilityLoading);

  // ---- Render helpers -------------------------------------------------------
  const renderCell = (therapist, date) => {
    const dateStr = toLocalDateStr(date);
    const cell = getCell(therapist, dateStr);
    const isToday = dateStr === todayStr;
    const isEmpty = !cell.hasWindows && cell.blocked.length === 0 && cell.absences.length === 0;
    const name = therapistFullName(therapist);

    return (
      <div
        key={dateStr}
        className={`tcg-cell ${isToday ? "tcg-cell-today" : ""} ${
          cell.absences.length > 0 ? "tcg-cell-absent" : ""
        }`}
        role="button"
        tabIndex={0}
        aria-label={`${name}, ${HEBREW_DAYS_SHORT[date.getDay()]} ${formatDayNumber(date)}`}
        title={
          canEditAvailability
            ? "לחצו לפתיחת היומן ולעריכת הזמינות"
            : "לחצו לפתיחת היומן"
        }
        onClick={() => handleCellOpen(therapist, date)}
        onKeyDown={(e) => handleCellKeyDown(e, therapist, date)}
      >
        {cell.absences.map((a) => (
          <div key={a.id} className={`tcg-absence ${a.allDay === false ? "tcg-absence-partial" : ""}`}>
            <span className="tcg-absence-label">
              {absenceLabel(a, {
                canSeeDetails: canSeeAbsenceDetails(a, { viewerId, isAdmin }),
              })}
              {a.allDay === false && a.startTime && a.endTime
                ? ` ${a.startTime}–${a.endTime}`
                : ""}
            </span>
          </div>
        ))}

        {cell.slots.map((w, i) => (
          <span key={`s-${i}`} className="tcg-window">
            {w.start}–{w.end}
          </span>
        ))}

        {cell.blocked.map((w, i) => (
          <span key={`b-${i}`} className="tcg-blocked">
            חסום {w.start}–{w.end}
          </span>
        ))}

        {cell.hasWindows && (
          <span
            className={`tcg-util ${
              cell.capacity > 0 && cell.booked >= cell.capacity ? "tcg-util-full" : ""
            }`}
          >
            {cell.booked}/{cell.capacity} תורים
          </span>
        )}

        {isEmpty && <span className="tcg-empty-cell">—</span>}
      </div>
    );
  };

  const renderSkeleton = () => <ContentLoader fill message="טוען זמינות צוות..." />;

  // ---- Render ---------------------------------------------------------------
  return (
    <div className="tcg">
      {/* Week navigation */}
      <div className="tcg-toolbar">
        <div className="tcg-nav">
          {/* RTL: right caret = previous, left caret = next */}
          <button
            type="button"
            className="tcg-nav-btn"
            onClick={() => shiftWeek(-1)}
            aria-label="שבוע קודם"
            title="שבוע קודם"
          >
            <CaretRight size={16} weight="bold" />
          </button>
          <button
            type="button"
            className={`tcg-nav-today ${isCurrentWeek ? "active" : ""}`}
            onClick={goToday}
          >
            <CalendarBlank size={14} weight={isCurrentWeek ? "fill" : "regular"} />
            שבוע נוכחי
          </button>
          <button
            type="button"
            className="tcg-nav-btn"
            onClick={() => shiftWeek(1)}
            aria-label="שבוע הבא"
            title="שבוע הבא"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        </div>
        <div className="tcg-week-label">{formatWeekRange(weekDays)}</div>
      </div>

      {/* Filters */}
      {therapists.length > 0 && (
        <div className="tcg-filters">
          <div className="tcg-chips" role="group" aria-label="סינון מטפלים">
            <button
              type="button"
              className={`tcg-chip ${selectedTherapistIds.length === 0 ? "active" : ""}`}
              onClick={() => setSelectedTherapistIds([])}
            >
              הכל
            </button>
            {therapists.map((t) => {
              const id = therapistKey(t);
              const active = selectedTherapistIds.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`tcg-chip ${active ? "active" : ""}`}
                  aria-pressed={active}
                  onClick={() => toggleTherapist(id)}
                >
                  {therapistFullName(t)}
                </button>
              );
            })}
          </div>

          <div className="tcg-filter-controls">
            <label
              className={`tcg-checkbox ${!todayInWeek ? "tcg-checkbox-disabled" : ""}`}
              title={!todayInWeek ? "זמין רק בשבוע הנוכחי" : undefined}
            >
              <input
                type="checkbox"
                checked={onlyAvailableToday}
                disabled={!todayInWeek}
                onChange={(e) => setOnlyAvailableToday(e.target.checked)}
              />
              <span>רק זמינים היום</span>
            </label>

            {activeRooms.length > 0 && (
              <label className="tcg-room-select">
                <Door size={14} />
                <select
                  value={roomFilter}
                  onChange={(e) => setRoomFilter(e.target.value)}
                  aria-label="סינון לפי חדר"
                >
                  <option value="">כל החדרים</option>
                  {activeRooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name || "חדר"}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Mobile day switcher */}
      {isMobile && (
        <div className="tcg-day-strip" role="tablist" aria-label="בחירת יום">
          {weekDays.map((d, i) => {
            const ds = toLocalDateStr(d);
            return (
              <button
                key={ds}
                type="button"
                role="tab"
                aria-selected={i === mobileDayIndex}
                className={`tcg-day-tab ${i === mobileDayIndex ? "active" : ""} ${
                  ds === todayStr ? "tcg-day-tab-today" : ""
                }`}
                onClick={() => setMobileDayIndex(i)}
              >
                <span className="tcg-day-tab-name">{HEBREW_DAYS_SHORT[i]}</span>
                <span className="tcg-day-tab-date">{formatDayNumber(d)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        renderSkeleton()
      ) : therapists.length === 0 ? (
        <div className="tcg-empty">
          <UsersThree size={40} weight="light" />
          <h3>אין מטפלים פעילים</h3>
          <p>הוסיפו מטפלים לקליניקה כדי לראות את זמינות הצוות</p>
        </div>
      ) : visibleTherapists.length === 0 ? (
        <div className="tcg-empty">
          <CalendarBlank size={40} weight="light" />
          <h3>אין מטפלים שתואמים לסינון</h3>
          <p>נסו לשנות את הסינון או לבחור "הכל"</p>
        </div>
      ) : (
        <div className="tcg-scroll">
          <div
            className="tcg-grid"
            style={{
              gridTemplateColumns: `${showTherapistNames ? "minmax(120px, 160px) " : ""}repeat(${
                daysToRender.length
              }, minmax(${isMobile ? "0" : "140px"}, 1fr))`,
            }}
          >
            {/* Header row */}
            {showTherapistNames && <div className="tcg-head tcg-head-name">מטפל/ת</div>}
            {daysToRender.map((d) => {
              const ds = toLocalDateStr(d);
              return (
                <div key={ds} className={`tcg-head ${ds === todayStr ? "tcg-head-today" : ""}`}>
                  <span className="tcg-head-day">{HEBREW_DAYS_SHORT[d.getDay()]}׳</span>
                  <span className="tcg-head-date">{formatDayNumber(d)}</span>
                </div>
              );
            })}

            {/* Therapist rows */}
            {visibleTherapists.map((t) => (
              <div key={therapistKey(t)} className="tcg-row">
                {showTherapistNames && (
                  <div className="tcg-name">
                    <span className="tcg-name-text">{therapistFullName(t)}</span>
                  </div>
                )}
                {daysToRender.map((d) => renderCell(t, d))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="tcg-legend" aria-label="מקרא">
        <span className="tcg-legend-item">
          <span className="tcg-window tcg-legend-swatch">09:00–13:00</span> שעות עבודה
        </span>
        <span className="tcg-legend-item">
          <span className="tcg-blocked tcg-legend-swatch">חסום</span> זמן חסום
        </span>
        <span className="tcg-legend-item">
          <span className="tcg-absence-swatch" /> היעדרות
        </span>
        <span className="tcg-legend-item">
          <span className="tcg-util tcg-legend-swatch">3/8 תורים</span> תורים / קיבולת
        </span>
      </div>

      {openCell && (
        <TherapistCalendarModal
          therapist={openCell.therapist}
          initialDate={openCell.date}
          onClose={() => setOpenCell(null)}
        />
      )}
    </div>
  );
}

export default TeamCoverageGrid;
